/**
 * I-864 household-size and Part 3 system answers.
 *
 * The I-864 has fields that are always a fixed value for an AOS filing:
 *  - Part 5 Line 2 (yourself) = 1 (USCIS mandate)
 *  - Part 5 Line 3 (spouse as immigrant) = 1 for marriage-based AOS
 *  - Part 3 Line 1 (immigrant is in the US) = true for I-485
 *  - Part 3 Line 28 (total immigrants) = 1 for the typical single case
 *
 * And fields derived from other answers:
 *  - Part 5 Line 6 (income contributors) = count of Part 6 group instances
 *  - Part 5 Line 7 (total household size) = sum of Lines 2–6
 *
 * These are never user-editable in either application. The download /
 * generate paths stamp them fresh on every fill so the PDF engine
 * always gets consistent values without requiring the user to enter
 * them manually.
 *
 * Algorithm: build a Map keyed by fieldId, seed with stored answers,
 * then overwrite with computed entries. Total size and contributor
 * count are always recomputed from constituent parts so they stay
 * consistent even when the user changes an input between fills.
 */
import {
  AOS_V1_SCHEMA_VERSION,
  defineFieldId,
  type Answer,
  type FieldId,
} from '@inside-us-aos/domain';

const YOURSELF_ID = defineFieldId('sponsor.household.yourself_count');
const SPOUSE_ID = defineFieldId('sponsor.household.spouse_as_immigrant_count');
const CHILDREN_ID = defineFieldId('sponsor.household.dependent_children_count');
const OTHER_DEPS_ID = defineFieldId('sponsor.household.other_dependents_count');
const CONTRIBUTORS_ID = defineFieldId('sponsor.household.income_contributors_count');
const TOTAL_ID = defineFieldId('sponsor.household.total_size');
const IN_US_ID = defineFieldId('sponsor.petition.immigrant_is_in_us');
const TOTAL_IMMIGRANTS_ID = defineFieldId('sponsor.petition.total_immigrants_count');
const CATEGORY_SELECTION_ID = defineFieldId('eligibility.category.selection');
const APPLYING_AS_ID = defineFieldId('eligibility.category.applying_as');
const I864_EXEMPTION_ID = defineFieldId(
  'eligibility.affidavit_of_support.exemption_basis',
);
const PUBLIC_CHARGE_EXEMPTION_ID = defineFieldId(
  'eligibility.public_charge.exemption_basis',
);
const PUBLIC_CHARGE_SUBJECT_ID = defineFieldId(
  'eligibility.public_charge.is_subject_to_inadmissibility_ground',
);
const PUBLIC_CHARGE_BENEFITS_SUMMARY_ID = defineFieldId(
  'eligibility.public_charge.benefits_history_summary',
);
const PUBLIC_CHARGE_RECEIVED_CASH_ID = defineFieldId(
  'eligibility.public_charge.received_listed_cash_benefits',
);
const PUBLIC_CHARGE_RECEIVED_INST_ID = defineFieldId(
  'eligibility.public_charge.received_long_term_institutionalization',
);
const SPONSOR_IS_SPONSORING_DERIVATIVES_ID = defineFieldId(
  'sponsor.derivative_immigrants.is_sponsoring',
);
const CHILD_LAST_NAME_ID = defineFieldId(
  'beneficiary.children.name.legal.last',
);
const CHILD_FIRST_NAME_ID = defineFieldId(
  'beneficiary.children.name.legal.first',
);
const CHILD_MIDDLE_NAME_ID = defineFieldId(
  'beneficiary.children.name.legal.middle',
);
const CHILD_DOB_ID = defineFieldId('beneficiary.children.birth.date');
const CHILD_APPLYING_CONCURRENTLY_ID = defineFieldId(
  'beneficiary.children.applying_concurrently',
);
const SI_LAST_ID = defineFieldId(
  'sponsor.sponsored_immigrants.name.legal.last',
);
const SI_FIRST_ID = defineFieldId(
  'sponsor.sponsored_immigrants.name.legal.first',
);
const SI_MIDDLE_ID = defineFieldId(
  'sponsor.sponsored_immigrants.name.legal.middle',
);
const SI_RELATIONSHIP_ID = defineFieldId(
  'sponsor.sponsored_immigrants.relationship_to_principal',
);
const SI_DOB_ID = defineFieldId('sponsor.sponsored_immigrants.date_of_birth');
const SPONSOR_CITIZENSHIP_STATUS_ID = defineFieldId('sponsor.citizenship.status');
const SPONSOR_CITIZENSHIP_ACQUIRED_VIA_ID = defineFieldId(
  'sponsor.citizenship.acquired_via',
);
const SPONSOR_BIRTH_COUNTRY_ID = defineFieldId('sponsor.birth.place.country');
const PETITIONER_NAT_CERT_ID = defineFieldId(
  'application.petitioner.naturalization.certificate_number',
);
const EOIR_ID = defineFieldId('eligibility.removal_proceedings.eoir_filing');
const PREV_LPR_APP_ID = defineFieldId(
  'beneficiary.prior_filings.applied_for_lpr_in_us',
);
const LPR_RESCINDED_ID = defineFieldId(
  'beneficiary.prior_filings.lpr_rescinded_under_ina_246',
);

// Sponsor mailing address → beneficiary's current-spouse physical
// address. For marriage-based filers (the typical AOS path) the
// applicant's spouse IS the sponsor, so the addresses are the same
// by definition. Each entry maps source → destination field ID.
const SPONSOR_ADDR_TO_SPOUSE_ADDR: ReadonlyArray<readonly [FieldId, FieldId]> = [
  [
    defineFieldId('sponsor.mailing_address.street_number_and_name'),
    defineFieldId(
      'beneficiary.current_spouse.physical_address.street_number_and_name',
    ),
  ],
  [
    defineFieldId('sponsor.mailing_address.unit_type'),
    defineFieldId('beneficiary.current_spouse.physical_address.unit_type'),
  ],
  [
    defineFieldId('sponsor.mailing_address.unit_number'),
    defineFieldId('beneficiary.current_spouse.physical_address.unit_number'),
  ],
  [
    defineFieldId('sponsor.mailing_address.city'),
    defineFieldId('beneficiary.current_spouse.physical_address.city'),
  ],
  [
    defineFieldId('sponsor.mailing_address.state'),
    defineFieldId('beneficiary.current_spouse.physical_address.state'),
  ],
  [
    defineFieldId('sponsor.mailing_address.zip_code'),
    defineFieldId('beneficiary.current_spouse.physical_address.zip_code'),
  ],
  [
    defineFieldId('sponsor.mailing_address.province'),
    defineFieldId('beneficiary.current_spouse.physical_address.province'),
  ],
  [
    defineFieldId('sponsor.mailing_address.postal_code'),
    defineFieldId('beneficiary.current_spouse.physical_address.postal_code'),
  ],
  [
    defineFieldId('sponsor.mailing_address.country'),
    defineFieldId('beneficiary.current_spouse.physical_address.country'),
  ],
];

const MARITAL_STATUS_ID = defineFieldId('beneficiary.marital.status');

// I-130 Pt4Line12 — "Provide the address in the United States where
// the beneficiary intends to live, if different from Item Numbers
// 11.a. - 11.h. If the address is the same, type or print 'SAME' in
// Item Number 12.a." Default to "SAME" for marriage-based filers
// who have NOT recorded a separate intended address.
const INTENDED_STREET_ID = defineFieldId(
  'beneficiary.intended_address.street_number_and_name',
);
const INTENDED_DIFFERS_ID = defineFieldId(
  'beneficiary.intended_address.differs_from_current',
);

// Eligibility categories whose Item 1 selection identifies a
// derivative beneficiary filing alongside a principal. Mirrors the
// `derivativeCategorySelections` list in packages/domain/src/fields/
// aosV1.ts — kept in sync by hand because the catalog is data and
// this is the engine. If the catalog list grows, this must too.
const DERIVATIVE_CATEGORIES: ReadonlySet<string> = new Set([
  'family-preference',
  'fiance',
  'eb-entrepreneur',
  'eb-worker',
  't-nonimmigrant',
  'u-nonimmigrant',
]);

function numericValue(stored: ReadonlyMap<FieldId, Answer>, fieldId: FieldId): number {
  const a = stored.get(fieldId);
  return a?.value.type === 'number' ? a.value.value : 0;
}

/**
 * Injects system-computed answers into the answer set before PDF fill.
 *
 * @param storedAnswers - Snapshot from `engine.getAllAnswers()`.
 * @param instancesByGroup - Repeatable group instance registry —
 *   used to count Part 6 household-member income rows.
 * @param applicationId - Application ID stamped on injected answers.
 * @returns New answer array including all stored answers plus computed
 *   overrides. Stored answers for the derived fields are overwritten.
 */
export function injectSystemAnswers(
  storedAnswers: readonly Answer[],
  instancesByGroup: ReadonlyMap<string, readonly string[]>,
  applicationId: string,
): readonly Answer[] {
  const now = new Date().toISOString();
  const base = {
    applicationId,
    schemaVersion: AOS_V1_SCHEMA_VERSION,
    updatedAt: now,
    updatedBy: 'system',
  } as const;

  // Singletons go in merged (no groupInstanceId). Repeatable instance
  // answers are preserved separately so the dedup-by-fieldId Map
  // doesn't drop all-but-the-last child / employer / household-member
  // row. Final return concatenates the two collections.
  const merged = new Map<FieldId, Answer>(
    storedAnswers
      .filter(a => a.groupInstanceId === undefined)
      .map(a => [a.fieldId, a]),
  );
  const repeatableAnswers: Answer[] = storedAnswers.filter(
    a => a.groupInstanceId !== undefined,
  );

  // --- I-864 Part 5 household size constants and derived values ---

  // Line 2: yourself — always 1 (USCIS mandate, never user-editable).
  merged.set(YOURSELF_ID, {
    ...base,
    fieldId: YOURSELF_ID,
    value: { type: 'number', value: 1 },
  });

  // Line 3: spouse — 0 for marriage-based AOS. The form's own NOTE on
  // Item 3 reads "Enter '0' if you already counted your spouse in Item
  // Number 1." For our scope (sponsor sponsoring their spouse), the
  // spouse IS the principal immigrant in Part 3 and is therefore
  // already counted in Item 1, so this line MUST be 0 to avoid
  // double-counting in Item 8 household size.
  //
  // 2026-05-27 fix: was hard-coded to 1, which made every i-864 we
  // generated double-count the beneficiary spouse. Confirmed against
  // the form's own instructions and reported by a customer.
  merged.set(SPOUSE_ID, {
    ...base,
    fieldId: SPOUSE_ID,
    value: { type: 'number', value: 0 },
  });

  // Line 7: income contributors — derived from Part 6 group instance
  // count. The catalog field is named `income_contributors_count`
  // but it maps to P5_Line7_SameResidence on the form (siblings /
  // parents / adult children submitting Form I-864A from the same
  // principal residence).
  const contributorCount =
    instancesByGroup.get('sponsor.household_members_income')?.length ?? 0;
  merged.set(CONTRIBUTORS_ID, {
    ...base,
    fieldId: CONTRIBUTORS_ID,
    value: { type: 'number', value: contributorCount },
  });

  // Line 8: household size total = sum of Items 1–7 per the form.
  //   L1 = number of immigrants sponsored on this affidavit (= principal
  //        immigrant + any Part 4 / Part 11 additions; for marriage-
  //        based AOS this defaults to 1 unless the user overrides).
  //   L2 = yourself = 1.
  //   L3 = spouse already counted in L1 → 0 (see above).
  //   L4 = dependent children not already in L1.
  //   L5 = other dependents not already in L1.
  //   L6 = previously sponsored persons (I-864 obligation still active).
  //   L7 = same-residence I-864A income contributors.
  //
  // The 10/17/24 template has no AcroForm widget for L1 or L8 — USCIS
  // expects an XFA-aware renderer to compute them at print time.
  // pdf-lib doesn't run XFA, so L1 / L8 stay blank in the rendered
  // packet; we still write the computed total here so the answer
  // store + audit trail carry the right number (case team can hand-
  // print on the rendered packet, or the USCIS reviewer sums Items
  // 1–7 themselves).
  //
  // L1 is read from `sponsor.petition.total_immigrants_count` if the
  // user explicitly set it; otherwise defaults to 1 (the typical
  // single-immigrant AOS case). Read here rather than after the
  // Part 3 injection block so the total computes against the same
  // value we'd inject (avoids a 1-off when the field is uninjected).
  const dependentChildren = numericValue(merged, CHILDREN_ID);
  const otherDependents = numericValue(merged, OTHER_DEPS_ID);
  const previouslySponsored = numericValue(
    merged,
    defineFieldId('sponsor.household.previously_sponsored_persons_count'),
  );
  const storedTotalImmigrants = numericValue(merged, TOTAL_IMMIGRANTS_ID);
  const totalImmigrantsSponsored =
    storedTotalImmigrants > 0 ? storedTotalImmigrants : 1;
  const totalSize =
    totalImmigrantsSponsored +
    1 +
    0 +
    dependentChildren +
    otherDependents +
    previouslySponsored +
    contributorCount;
  merged.set(TOTAL_ID, {
    ...base,
    fieldId: TOTAL_ID,
    value: { type: 'number', value: totalSize },
  });

  // --- I-864 Part 3 constants (inject only when not already stored) ---

  // Line 1: immigrant is in the US — always true for AOS.
  if (!merged.has(IN_US_ID)) {
    merged.set(IN_US_ID, {
      ...base,
      fieldId: IN_US_ID,
      value: { type: 'boolean', value: true },
    });
  }

  // Line 28: total immigrants — default 1 for single-immigrant AOS.
  if (!merged.has(TOTAL_IMMIGRANTS_ID)) {
    merged.set(TOTAL_IMMIGRANTS_ID, {
      ...base,
      fieldId: TOTAL_IMMIGRANTS_ID,
      value: { type: 'number', value: 1 },
    });
  }

  // --- I-485 Part 2 Item 2: Principal vs Derivative Applicant ---
  //
  // The Pt2Line2_CB[0/1] checkboxes ("Principal Applicant" /
  // "Derivative Applicant") are driven by the eligibility category.
  // Brandan and other family-immediate filers are principals;
  // family-preference / fiance / employment-based / T-nonimmigrant /
  // U-nonimmigrant filers are derivatives. We auto-derive so the
  // applicant doesn't have to answer the same question twice.
  //
  // Only override when the applicant hasn't explicitly answered —
  // a deliberate override (e.g., an edge-case filer correcting our
  // inference) survives.
  if (!merged.has(APPLYING_AS_ID)) {
    const categoryAnswer = merged.get(CATEGORY_SELECTION_ID);
    if (categoryAnswer?.value.type === 'select') {
      const isDerivative = DERIVATIVE_CATEGORIES.has(categoryAnswer.value.value);
      merged.set(APPLYING_AS_ID, {
        ...base,
        fieldId: APPLYING_AS_ID,
        value: {
          type: 'select',
          value: isDerivative ? 'derivative' : 'principal',
        },
      });
    }
  }

  // --- I-485 Part 3: Affidavit of Support exemption basis ---
  //
  // Marriage-based, family-based, employment-based filers MUST
  // submit Form I-864 — none of the exemption categories (a–d)
  // apply. Default to `not_exempt_required` (Item 1.f) so the
  // checkbox renders without the applicant having to think about
  // the SSA/under-18/widower/VAWA edge cases. If they actually
  // qualify for an exemption, the intake lets them override.
  if (!merged.has(I864_EXEMPTION_ID)) {
    merged.set(I864_EXEMPTION_ID, {
      ...base,
      fieldId: I864_EXEMPTION_ID,
      value: { type: 'select', value: 'not_exempt_required' },
    });
  }

  // --- I-485 Part 9 Item 56 — Public-Charge exemption ---
  //
  // 24 boxes for exempt categories + 1 "I do not fall under any of
  // the exempt categories listed above, complete items 57-67". For
  // marriage-based and most employment-based filers, none of the
  // exempt categories apply, so default to the "not exempt" box.
  // The applicant can override in intake if they actually qualify.
  if (!merged.has(PUBLIC_CHARGE_EXEMPTION_ID)) {
    merged.set(PUBLIC_CHARGE_EXEMPTION_ID, {
      ...base,
      fieldId: PUBLIC_CHARGE_EXEMPTION_ID,
      value: { type: 'select', value: 'not_exempt_complete_57_67' },
    });
  }

  // --- I-485 Part 9 Item 61 — subject to the public-charge ground ---
  //
  // Every marriage-based AOS filer IS subject to the public-charge
  // ground; the entire reason the petitioner files Form I-864 is to
  // defeat that ground via the Affidavit of Support. Default to true
  // so the user never has to think about this conceptually-loaded
  // yes/no, and so Items 57-67 visibility fires correctly.
  if (!merged.has(PUBLIC_CHARGE_SUBJECT_ID)) {
    merged.set(PUBLIC_CHARGE_SUBJECT_ID, {
      ...base,
      fieldId: PUBLIC_CHARGE_SUBJECT_ID,
      value: { type: 'boolean', value: true },
    });
  }

  // --- I-485 Part 9 Items 63 + 64 — derive from blanket summary ---
  //
  // Replaces two raw yes/no intake questions with a single 4-way
  // select (`benefits_history_summary`). The summary's value drives
  // both boolean fields so the engine can still write the PDF
  // checkboxes via the existing widgetValueProjection on Items 63/64.
  //
  //   summary value           → received_cash | received_inst
  //   none                    →      false    |     false
  //   cash_only               →      true     |     false
  //   institutional_only      →      false    |     true
  //   both                    →      true     |     true
  //
  // If the applicant never answers the summary (legacy data path),
  // both booleans stay unset and the existing fallback shows the raw
  // questions via the catalog's visibility rule.
  const summary = merged.get(PUBLIC_CHARGE_BENEFITS_SUMMARY_ID);
  if (summary && summary.value.type === 'select') {
    const v = summary.value.value;
    const cash = v === 'cash_only' || v === 'both';
    const inst = v === 'institutional_only' || v === 'both';
    if (!merged.has(PUBLIC_CHARGE_RECEIVED_CASH_ID)) {
      merged.set(PUBLIC_CHARGE_RECEIVED_CASH_ID, {
        ...base,
        fieldId: PUBLIC_CHARGE_RECEIVED_CASH_ID,
        value: { type: 'boolean', value: cash },
      });
    }
    if (!merged.has(PUBLIC_CHARGE_RECEIVED_INST_ID)) {
      merged.set(PUBLIC_CHARGE_RECEIVED_INST_ID, {
        ...base,
        fieldId: PUBLIC_CHARGE_RECEIVED_INST_ID,
        value: { type: 'boolean', value: inst },
      });
    }
  }

  // --- I-485 Part 2 Item 1, Part 4 Items 5 & 6 — default No ---
  //
  // The vast majority of applicants answer No to:
  //   - EOIR removal-proceedings filing (Part 2 Item 1)
  //   - "Have you previously applied for permanent residence while
  //      in the United States?" (Part 4 Item 5)
  //   - "Have you EVER held LPR status which was later rescinded
  //      under INA section 246?" (Part 4 Item 6)
  // Default to false (No box checked) so the rendered packet is
  // complete. Applicants whose answer is Yes override explicitly
  // in the intake.
  for (const id of [EOIR_ID, PREV_LPR_APP_ID, LPR_RESCINDED_ID]) {
    if (!merged.has(id)) {
      merged.set(id, {
        ...base,
        fieldId: id,
        value: { type: 'boolean', value: false },
      });
    }
  }

  // Eligibility bulk default-No REMOVED 2026-05-27.
  //
  // Every Yes/No on the I-485 / I-130 / I-864 is a sworn statement
  // under penalty of perjury. Stamping "No" via the PDF engine
  // without the applicant ever seeing the question is not a defensible
  // posture — the applicant must affirmatively answer (either by
  // checking each box in intake, or by clicking "None apply" in the
  // EligibilityGroupedRenderer, which writes an explicit `false` for
  // every field in the block). The fix is to expand the catalog +
  // intake so every form item has a question the applicant can
  // answer, not to silently fill it server-side.

  // --- Current Spouse Physical Address auto-derive ---
  //
  // For marriage-based AOS the petitioner (sponsor) IS the spouse,
  // so the spouse's current physical address equals the sponsor's
  // mailing address. Copy each sponsor.mailing_address.* answer
  // into the corresponding beneficiary.current_spouse.physical_
  // address.* slot when the applicant is currently married /
  // legally separated and hasn't explicitly answered.
  const maritalStatus = merged.get(MARITAL_STATUS_ID)?.value;
  const isMarriedOrSeparated =
    maritalStatus?.type === 'select' &&
    (maritalStatus.value === 'married' ||
      maritalStatus.value === 'legally_separated');
  if (isMarriedOrSeparated) {
    for (const [sourceId, destId] of SPONSOR_ADDR_TO_SPOUSE_ADDR) {
      if (merged.has(destId)) continue;
      const sourceAnswer = merged.get(sourceId);
      if (sourceAnswer === undefined) continue;
      merged.set(destId, {
        ...base,
        fieldId: destId,
        value: sourceAnswer.value,
      });
    }
  }

  // --- I-130 Pt2Line23 — Petitioner citizenship acquired via ---
  //
  // Item 37 on the I-130: 3 checkboxes for Birth in U.S. / Naturalization /
  // Parents. Derive from existing data so the typical case auto-fills:
  //   - sponsor born in US → birth_in_us
  //   - naturalization certificate number set → naturalization
  //   - otherwise (LPR sponsors, derived-from-parent cases) → leave blank
  //
  // Only fires when sponsor.citizenship.status === 'us_citizen' (LPR
  // petitioners don't fill Item 37). Preserves explicit user override.
  const sponsorStatus = merged.get(SPONSOR_CITIZENSHIP_STATUS_ID)?.value;
  const sponsorIsCitizen =
    sponsorStatus?.type === 'select' && sponsorStatus.value === 'us_citizen';
  if (sponsorIsCitizen && !merged.has(SPONSOR_CITIZENSHIP_ACQUIRED_VIA_ID)) {
    const birthCountry = merged.get(SPONSOR_BIRTH_COUNTRY_ID)?.value;
    const hasNaturalizationCert =
      merged.get(PETITIONER_NAT_CERT_ID)?.value?.type === 'text' &&
      ((merged.get(PETITIONER_NAT_CERT_ID)?.value as { value: string })
        .value?.length ?? 0) > 0;
    let derived: 'birth_in_us' | 'naturalization' | null = null;
    if (
      birthCountry?.type === 'select' &&
      typeof birthCountry.value === 'string' &&
      birthCountry.value.toUpperCase() === 'US'
    ) {
      derived = 'birth_in_us';
    } else if (hasNaturalizationCert) {
      derived = 'naturalization';
    }
    if (derived !== null) {
      merged.set(SPONSOR_CITIZENSHIP_ACQUIRED_VIA_ID, {
        ...base,
        fieldId: SPONSOR_CITIZENSHIP_ACQUIRED_VIA_ID,
        value: { type: 'select', value: derived },
      });
    }
  }

  // --- I-130 Pt4Line12 — Intended U.S. Address ---
  //
  // USCIS instruction: type "SAME" in Item 12.a when the
  // beneficiary's intended address equals the current address
  // (Items 11.a–11.h). If `differs_from_current` is explicitly
  // false (or unset) and the applicant hasn't entered a custom
  // intended-address street, stamp "SAME" so the rendered Pt4Line12a
  // widget doesn't print blank. Don't override when the applicant
  // has explicitly provided a different intended address.
  const intendedDiffers = merged.get(INTENDED_DIFFERS_ID)?.value;
  const intendedDiffersTrue =
    intendedDiffers?.type === 'boolean' && intendedDiffers.value === true;
  if (!intendedDiffersTrue && !merged.has(INTENDED_STREET_ID)) {
    merged.set(INTENDED_STREET_ID, {
      ...base,
      fieldId: INTENDED_STREET_ID,
      value: { type: 'text', value: 'SAME' },
    });
  }

  // --- I-864 Part 4 sponsored immigrants — auto-derive from children ---
  //
  // Marriage-based AOS with derivative children: the beneficiary's
  // children section already collected name/middle/DOB and an
  // applying_concurrently flag for each child. Mirror that into the
  // I-864 P3 rows so the user doesn't enter the same data twice.
  // For each beneficiary.children row with applying_concurrently=true:
  //   - copy last/first/middle name + birth date
  //   - stamp relationship = "Child"
  //   - reuse the child's groupInstanceId on the sponsor side so the
  //     PDF engine writes the row in the same slot
  // The summary boolean (sponsor.derivative_immigrants.is_sponsoring)
  // turns true if at least one concurrent child exists.
  //
  // Existing sponsor.sponsored_immigrants answers (user-entered or
  // operator-overridden) are preserved — we only insert when the
  // (fieldId, groupInstanceId) pair isn't already present.
  const existingSponsoredKeys = new Set<string>(
    repeatableAnswers
      .filter(a => (a.fieldId as string).startsWith('sponsor.sponsored_immigrants'))
      .map(a => `${a.fieldId}|${a.groupInstanceId}`),
  );
  const childrenInstanceMeta = new Map<
    string,
    {
      last?: string;
      first?: string;
      middle?: string;
      dob?: string;
      concurrent?: boolean;
    }
  >();
  for (const a of repeatableAnswers) {
    if (!a.groupInstanceId) continue;
    const meta = childrenInstanceMeta.get(a.groupInstanceId) ?? {};
    if (a.fieldId === CHILD_LAST_NAME_ID && a.value.type === 'text') {
      meta.last = a.value.value;
    } else if (a.fieldId === CHILD_FIRST_NAME_ID && a.value.type === 'text') {
      meta.first = a.value.value;
    } else if (a.fieldId === CHILD_MIDDLE_NAME_ID && a.value.type === 'text') {
      meta.middle = a.value.value;
    } else if (a.fieldId === CHILD_DOB_ID && a.value.type === 'date') {
      meta.dob = a.value.value;
    } else if (
      a.fieldId === CHILD_APPLYING_CONCURRENTLY_ID &&
      a.value.type === 'boolean'
    ) {
      meta.concurrent = a.value.value;
    } else {
      continue;
    }
    childrenInstanceMeta.set(a.groupInstanceId, meta);
  }
  const concurrentChildEntries = [...childrenInstanceMeta.entries()].filter(
    ([, meta]) => meta.concurrent === true,
  );
  if (concurrentChildEntries.length > 0) {
    if (!merged.has(SPONSOR_IS_SPONSORING_DERIVATIVES_ID)) {
      merged.set(SPONSOR_IS_SPONSORING_DERIVATIVES_ID, {
        ...base,
        fieldId: SPONSOR_IS_SPONSORING_DERIVATIVES_ID,
        value: { type: 'boolean', value: true },
      });
    }
    const derivedAnswers: Answer[] = [];
    const push = (
      groupInstanceId: string,
      fieldId: FieldId,
      value: Answer['value'],
    ): void => {
      const key = `${fieldId}|${groupInstanceId}`;
      if (existingSponsoredKeys.has(key)) return;
      derivedAnswers.push({
        ...base,
        fieldId,
        groupInstanceId,
        value,
      });
    };
    for (const [instanceId, meta] of concurrentChildEntries) {
      if (meta.last !== undefined) {
        push(instanceId, SI_LAST_ID, { type: 'text', value: meta.last });
      }
      if (meta.first !== undefined) {
        push(instanceId, SI_FIRST_ID, { type: 'text', value: meta.first });
      }
      if (meta.middle !== undefined) {
        push(instanceId, SI_MIDDLE_ID, { type: 'text', value: meta.middle });
      }
      if (meta.dob !== undefined) {
        push(instanceId, SI_DOB_ID, { type: 'date', value: meta.dob });
      }
      push(instanceId, SI_RELATIONSHIP_ID, {
        type: 'text',
        value: 'Child',
      });
    }
    repeatableAnswers.push(...derivedAnswers);
  } else if (!merged.has(SPONSOR_IS_SPONSORING_DERIVATIVES_ID)) {
    merged.set(SPONSOR_IS_SPONSORING_DERIVATIVES_ID, {
      ...base,
      fieldId: SPONSOR_IS_SPONSORING_DERIVATIVES_ID,
      value: { type: 'boolean', value: false },
    });
  }

  return [...merged.values(), ...repeatableAnswers];
}
